import { broadcastAuthSessionFailure } from './authEvents.ts'
import {
  checkAuthSession,
  type AuthSessionCheckResult,
  type AuthSessionFailure
} from './authSession.ts'

type SessionCheck = () => Promise<AuthSessionCheckResult>
type FailureBroadcast = (failure: AuthSessionFailure) => void

export class AuthSessionFailureProbe {
  private inFlight: Promise<void> | null = null
  private readonly check: SessionCheck
  private readonly broadcast: FailureBroadcast

  constructor(
    check: SessionCheck = () => checkAuthSession(),
    broadcast: FailureBroadcast = failure => {
      broadcastAuthSessionFailure(failure, 'http')
    }
  ) {
    this.check = check
    this.broadcast = broadcast
  }

  run(): Promise<void> {
    if (this.inFlight) return this.inFlight
    const probe = this.check()
      .then(result => {
        if (result.status === 'failure') this.broadcast(result.failure)
      })
      .finally(() => {
        if (this.inFlight === probe) this.inFlight = null
      })
    this.inFlight = probe
    return probe
  }
}

const businessRequestSessionProbe = new AuthSessionFailureProbe()

export async function rethrowAfterAuthSessionProbe(
  error: unknown,
  probe: AuthSessionFailureProbe = businessRequestSessionProbe
): Promise<never> {
  if (!isAbortError(error)) {
    try {
      await probe.run()
    } catch {
      // The original business request error remains the caller-facing cause.
    }
  }
  throw error
}

export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}
